import { supabase } from '../lib/supabase';

export const esgService = {
  // Create ESG report
  async createESGReport(reportData) {
    try {
      const { data, error } = await supabase?.from('esg_reports')?.insert([reportData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get organization's ESG reports
  async getOrganizationESGReports(organizationId, isPublic = false) {
    try {
      let query = supabase?.from('esg_reports')?.select(`
          *,
          organizations!inner(name, industry, logo_url)
        `)?.eq('organization_id', organizationId);

      if (isPublic) {
        query = query?.eq('is_public', true);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get public ESG reports for discovery
  async getPublicESGReports(filters = {}) {
    try {
      let query = supabase?.from('esg_reports')?.select(`
          *,
          organizations!inner(name, industry, logo_url, is_501c3)
        `)?.eq('is_public', true);

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.metric_type) {
        query = query?.eq('metric_type', filters?.metric_type);
      }

      if (filters?.industry) {
        query = query?.eq('organizations.industry', filters?.industry);
      }

      const { data, error } = await query?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update ESG report
  async updateESGReport(reportId, updates) {
    try {
      const { data, error } = await supabase?.from('esg_reports')?.update(updates)?.eq('id', reportId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete ESG report
  async deleteESGReport(reportId) {
    try {
      const { error } = await supabase?.from('esg_reports')?.delete()?.eq('id', reportId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Get ESG metrics summary for organization
  async getESGSummary(organizationId) {
    try {
      const { data, error } = await supabase?.from('esg_reports')?.select('category, metric_type, current_value, target_value, measurement_unit')?.eq('organization_id', organizationId)?.eq('is_public', true);

      if (error) throw error;

      // Group by category and calculate progress
      const summary = data?.reduce((acc, report) => {
        const category = report?.category;
        if (!acc?.[category]) {
          acc[category] = {
            metrics: [],
            totalProgress: 0,
            count: 0
          };
        }

        const progress = report?.target_value ? 
          ((report?.current_value || 0) / report?.target_value) * 100 : 0;

        acc?.[category]?.metrics?.push({
          type: report?.metric_type,
          current: report?.current_value,
          target: report?.target_value,
          unit: report?.measurement_unit,
          progress: Math.min(progress, 100)
        });

        acc[category].totalProgress += progress;
        acc[category].count += 1;

        return acc;
      }, {});

      // Calculate average progress for each category
      Object.keys(summary || {})?.forEach(category => {
        summary[category].averageProgress = 
          summary?.[category]?.totalProgress / summary?.[category]?.count;
      });

      return { data: summary, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};